import { Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { useAtom } from 'jotai';
import { sortBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import useStyles from '@/features/TicketCreateDialog/TicketCreateDialog.styles';
import useStaticData from '@/hooks/StaticData.hooks';
import { organizationsAtom } from '@/pages/Organizations';
import { TICKET_TYPE, ticketDialogAtom } from '@/pages/Ticket';

import {
  CATEGORY_ID,
  canSelectKanatiCategory,
  isKanatiCategory
} from '../../TicketCreateDialog.constants';

const ServiceForm = () => {
  const [ticket] = useAtom(ticketDialogAtom);
  const [organizations] = useAtom(organizationsAtom);

  const {
    control,
    setValue,
    formState: { errors }
  } = useFormContext();
  const organizationId = useWatch({ control, name: 'organization_id' });
  const categoryId = useWatch({ control, name: 'ticket_category' });
  // organization settings come with the organizations list, which every user can read
  const organization = organizations.find((org) => org.id === organizationId);
  const kanatiEnabled =
    ticket.ticket_type === TICKET_TYPE.SERVICE &&
    organization?.organization_settings?.ai_enable_submission === true;
  const isIrOrganization = organization?.is_ir === true;
  const { categories, isFetching } = useStaticData();
  const { classes } = useStyles({ errors });

  const [categoriesList, urgentCategory, kanatiCategories] = useMemo(() => {
    const serviceCategories = sortBy(categories, 'name').filter(
      (category) => category.ticket_type === ticket.ticket_type
    );

    return [
      serviceCategories.filter(
        (category) =>
          category.id !== CATEGORY_ID.ESCALATION_REQUEST &&
          category.id !== CATEGORY_ID.URGENT &&
          !isKanatiCategory(category.id)
      ),
      serviceCategories.find((category) => category.id === CATEGORY_ID.URGENT),
      kanatiEnabled
        ? serviceCategories.filter((category) =>
            canSelectKanatiCategory(category.id, isIrOrganization)
          )
        : []
    ];
  }, [categories, ticket.ticket_type, kanatiEnabled, isIrOrganization]);

  useEffect(() => {
    if (
      isKanatiCategory(categoryId) &&
      (!kanatiEnabled || !canSelectKanatiCategory(categoryId, isIrOrganization))
    ) {
      setValue('ticket_category', '', { shouldValidate: true });
    }
  }, [categoryId, kanatiEnabled, isIrOrganization, setValue]);

  return (
    <>
      {!isFetching && (
        <Stack className={classes.ticketCreateDialogFormElement}>
          <InputLabel htmlFor="category-select">Category *</InputLabel>
          <FormControl fullWidth>
            <Controller
              control={control}
              name="ticket_category"
              rules={{
                required: true
              }}
              render={({ field, formState }) => (
                <Select
                  id="category-select"
                  error={!!formState.errors.ticket_category}
                  {...field}
                >
                  <MenuItem value={urgentCategory?.id}>
                    <Stack
                      direction="row"
                      sx={{ justifyContent: 'space-between', flex: 1, gap: 2 }}
                    >
                      <Typography
                        sx={{ fontWeight: 'bold' }}
                        color="warning.main"
                      >
                        {urgentCategory?.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        Use for urgent issues
                      </Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem
                    value=""
                    disabled
                    sx={{ backgroundColor: 'secondary.main', py: 0.5 }}
                  />
                  {categoriesList.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                  {kanatiCategories.length > 0 && (
                    <MenuItem
                      value=""
                      disabled
                      sx={{ backgroundColor: 'secondary.main', py: 0.5 }}
                    />
                  )}
                  {kanatiCategories.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Stack>
      )}
    </>
  );
};

export default ServiceForm;
